import React from 'react';
import { ArrowUpRightIcon, BusIcon, ClockIcon, MapPinIcon } from 'lucide-react';
import { EmptyState } from '../ui/EmptyState';
import { ButtonExternal } from '../ui/Button';
import type { LocalChurch } from '../../data/churches';
import { externalLinks } from '../../data/site';

/**
 * Church directory. Renders verified entries from data/churches.ts and falls
 * back to a useful public empty state while no verified data exists.
 */
export function ChurchDirectory({
  churches
}: {
  churches: LocalChurch[];
}) {
  if (churches.length === 0) {
    return <EmptyState label="Coming soon" title="Our local church guide is being updated" copy="Need help finding a church now? Reach out to UCF BCM and a leader can help you explore local options." data-id="element-56" data-mp="eabtnokj0ej7">
        <ButtonExternal href={externalLinks.getConnectedForm} data-id="element-57" data-mp="2tz3qbp07z6ae">Ask a BCM Leader</ButtonExternal>
        <ButtonExternal href={externalLinks.email} variant="secondary" data-id="element-58" data-mp="2py1qb5yhaq7d">
          Email UCF BCM
        </ButtonExternal>
      </EmptyState>;
  }
  return <ul className="grid gap-5 sm:grid-cols-2" data-id="element-59" data-mp="25j6sgzrj4qqf">
      {churches.map(church => <li key={church.id} data-id="element-60" data-mp="2ah8cpo0oyw3h">
          <article className="flex h-full flex-col rounded-[22px] border-2 border-brand-navy bg-white p-6" data-id="element-61" data-mp="2aedsnxllbdal">
            {church.bcmStudentsAttend ? <span className="mb-3 w-fit rounded-full bg-brand-yellow px-3 py-1 font-meta text-[11px] font-semibold uppercase tracking-[0.14em] text-[#3d2b00]" data-id="element-62" data-mp="2k7bdiae21ecd">
                BCM students attend here
              </span> : null}
            <h3 className="font-display text-xl font-bold leading-snug text-brand-navy" data-id="element-63" data-mp="2t91278gjxz6g">
              {church.name}
            </h3>
            {church.description ? <p className="mt-2 font-sans text-[15px] leading-relaxed text-ink" data-id="element-64" data-mp="333sy87uhkdz4">
                {church.description}
              </p> : null}

            <dl className="mt-4 space-y-2 font-meta text-[13px] text-ink" data-id="element-65" data-mp="18qurfy7zvzgj">
              {church.address ? <div className="flex items-start gap-2" data-id="element-66" data-mp="10pn5kci8w15c">
                  <dt className="sr-only" data-id="element-67" data-mp="10shsqbsn802g">Address</dt>
                  <MapPinIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-brand-navy" data-id="element-68" data-mp="wpv4ekv6kxa0" />
                  <dd data-id="element-69" data-mp="1apmi0khscbnh">
                    {church.address}
                    {church.distanceFromUcf ? ` · ${church.distanceFromUcf}` : ''}
                  </dd>
                </div> : null}
              {church.serviceTimes && church.serviceTimes.length > 0 ? <div className="flex items-start gap-2" data-id="element-70" data-mp="1foh887i312hm">
                  <dt className="sr-only" data-id="element-71" data-mp="1flv0a6ra4kyv">Service times</dt>
                  <ClockIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-brand-navy" data-id="element-72" data-mp="1tdred6w44uc9" />
                  <dd data-id="element-73" data-mp="1ohap29d6g9xl">{church.serviceTimes.join(' • ')}</dd>
                </div> : null}
              {church.transportation ? <div className="flex items-start gap-2" data-id="element-74" data-mp="1ye5ocuh9yecx">
                  <dt className="sr-only" data-id="element-75" data-mp="1yfqhcfnxxp8o">Transportation</dt>
                  <BusIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-brand-navy" data-id="element-76" data-mp="2hzfnoumv9978" />
                  <dd data-id="element-77" data-mp="2m22goyv9x060">{church.transportation}</dd>
                </div> : null}
            </dl>

            {church.website ? <a href={church.website} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-1 font-meta text-[13px] font-semibold text-brand-navy underline underline-offset-4 hover:no-underline" data-id="element-78" data-mp="281jcy3xucg9n">
                Visit website
                <ArrowUpRightIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-79" data-mp="1pbjrrs54flbi" />
              </a> : null}
          </article>
        </li>)}
    </ul>;
}